/**
 * AppointmentStatusManager — canonical action row + dialog mount point
 * for every status transition an appointment can take.
 *
 * Flutter parity: `scrips_core/lib/widgets/general/appointment_status.dart`
 * is a single widget reused across the row (DayListCalendar /
 * AppointmentListView), the bottom of the appointment popup card, and the
 * full-page edit screen. This React component plays the same role.
 *
 * Owns:
 *   - Status mutation (useUpdateAppointmentStatus — Flutter parity via
 *     PUT /api/Appointment/Status, with optimistic update + rollback).
 *   - Reschedule + Cancel dialogs (both mounted internally so consumers
 *     don't have to wire them up again).
 *   - The action vocabulary lookup (getPopupActionsFor /
 *     getRowActionsFor in appointment-actions.ts).
 *
 * Doesn't own:
 *   - "Edit" navigation (consumer's onEdit callback — popup closes itself
 *     and navigates; row opens an inline editor).
 *   - The print-sticker side button (consumer-specific).
 *   - Parent-frame closing (consumer-specific via `onAfterMutation`).
 *
 * Variants:
 *   - `popup`  — Flutter's popup footer set. Status transitions gated by
 *                isToday; Reschedule / Cancel always available unless
 *                terminal. Used by view-appointment-popup.
 *   - `row`    — Flutter's row action set. Cancelled / Fulfilled return
 *                a narrower set; row variant is wired up when the
 *                DayListCalendar / AppointmentListView consumers migrate
 *                to this component (PROD-379 follow-up).
 *
 * Optimistic invalidation:
 *   The `useUpdateAppointmentStatus` hook already snapshots + patches the
 *   `['appointments']` query cache and rolls back on error. We additionally
 *   invalidate the `['appointment-details']` key here so the popup's own
 *   status badge + billing-tab gate refresh after a transition.
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useStatusList } from '@/services/scheduling/use-status-list';
import { useUpdateAppointmentStatus } from '@/scheduling/services/use-update-appointment-status';
import { RescheduleAppointmentDialog } from './reschedule-appointment-dialog';
import { CancelAppointmentDialog } from './cancel-appointment-dialog';
import { isToday } from './popup-cancel-rule';
import { LogIn, UserX, CalendarCheck, CalendarX2, Undo2, Pencil, CalendarPlus, RefreshCw } from 'lucide-react';
import {
  getPopupActionsFor,
  getRowActionsFor,
  getPanelActionsFor,
  statusCodeForAction,
  type AppointmentAction,
  type AppointmentActionId,
  type ActionTone,
} from '@/scheduling/lib/appointment-actions';
import { isWithinStatusButtonWindow } from '@/scheduling/lib/status-button-window';
import type { LucideIcon } from 'lucide-react';

/** Lucide icons for popup footer buttons — thicker strokes, consistent with the rest of the app. */
const ACTION_ICON: Partial<Record<AppointmentActionId, LucideIcon>> = {
  'mark-arrived': LogIn,
  'mark-checked-in': CalendarCheck,
  'mark-no-show': UserX,
  'confirm': CalendarCheck,
  'unmark-arrived': Undo2,
  'unmark-checked-in': Undo2,
  'unmark-no-show': Undo2,
  'reschedule': RefreshCw,
  'cancel': CalendarX2,
  'edit': Pencil,
  'follow-up': CalendarPlus,
};

/** Short labels for the popup footer — the icon carries most of the meaning. */
const SHORT_LABEL: Partial<Record<AppointmentActionId, string>> = {
  'mark-arrived': 'Arrived',
  'mark-checked-in': 'Check in',
  'mark-no-show': 'No show',
  'confirm': 'Confirm',
  'unmark-arrived': 'Undo arrived',
  'unmark-checked-in': 'Undo check-in',
  'unmark-no-show': 'Undo no-show',
  'reschedule': 'Reschedule',
  'cancel': 'Cancel',
  'edit': 'Edit',
  'follow-up': 'Follow-up',
};
import {
  arrivedGuardStatus,
  arrivedGuardMessage,
  ARRIVED_GUARD_TITLE,
  ARRIVED_GUARD_PROCEED,
  type ArrivedGuardKind,
} from '@/scheduling/lib/arrived-guard';
import { guardPatientFromAppointment } from '@/scheduling/lib/guard-patient-from-appointment';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type {
  AppointmentResponse,
  AppointmentStatus,
} from '@/types/scheduling-api';

export interface AppointmentStatusManagerProps {
  appt: AppointmentResponse;
  /**
   * Rule-set selector. The variant does NOT change layout, sizing, or
   * dialog behavior — it only selects which `appointment-actions.ts`
   * helper (`getPopupActionsFor` vs `getRowActionsFor`) decides which
   * buttons render for a given status.
   *
   *   - `'popup'`  — popup-footer rule set: status transitions gated
   *                  by isToday, Reschedule / Cancel always available
   *                  unless terminal. Used by the calendar popup AND
   *                  the ViewAppointmentPanel footer (PROD-377) — both
   *                  are full-width footer surfaces with the same
   *                  rule set, so this variant covers both.
   *   - `'row'`   — narrow row-action set for DayListCalendar /
   *                  AppointmentListView rows. Tighter set, hides some
   *                  actions for terminal statuses.
   *   - `'panel'` — LEFT edit-panel vertical menu (Flutter
   *                  `create_appointment_widget.dart:2562-2707`): full-width
   *                  text buttons stacked above nothing else. 3-day window
   *                  gate, full-word labels, no reschedule/follow-up, cancel
   *                  included. Used inside CreateAppointmentPanel (edit mode).
   *
   * Sizing, close handlers, and parent-frame closing are all owned by
   * the caller (via `onAfterMutation`). Nothing in this component
   * assumes a popup-shaped container.
   */
  variant?: 'popup' | 'row' | 'panel';
  /** Optional pencil callback (consumer-specific navigation). */
  onEdit?: () => void;
  /**
   * Follow-up callback (PROD-987). Fired when the 'follow-up' action is
   * clicked (only present when Fulfilled + non-recurring per
   * getPopupActionsFor). The consumer opens the create panel pre-filled
   * from the source appointment. When absent, the action falls through to
   * a no-op (parity with the row variant, whose consumer wires it).
   */
  onFollowUp?: () => void;
  /**
   * Optional reschedule handler. When provided, the Reschedule action delegates
   * to it (e.g. the popup opens the inline left panel in reschedule mode —
   * Flutter parity) instead of the built-in stopgap date/time dialog. When
   * absent, the built-in dialog is used (day-list / other consumers).
   */
  onReschedule?: () => void;
  /**
   * Called after any status mutation OR after a successful
   * reschedule/cancel. Lets a popup parent close itself etc.
   */
  onAfterMutation?: () => void;
  /**
   * Suppress the Cancel action. Used by the full appointment screen
   * (ViewAppointmentPanelPage) so Cancel lives ONLY in the left edit panel
   * (Flutter parity: the full screen has no standalone Cancel — it's owned by
   * the embedded edit panel / popup / 3-dots). Default false.
   */
  hideCancel?: boolean;
  /**
   * Let the parent own the Cancel dialog's open state, for a Cancel button drawn elsewhere (the popup's header).
   * With `hideCancel` the footer has no Cancel, the dialog is still mounted here. Absent = the manager owns it.
   */
  cancelOpen?: boolean;
  onCancelOpenChange?: (open: boolean) => void;
  /**
   * Told when a status update starts and settles, so a Cancel drawn elsewhere can be disabled while one is in
   * flight (the footer Cancel reads `updateStatus.isPending` for the same rule).
   */
  onStatusPendingChange?: (pending: boolean) => void;
}

export function AppointmentStatusManager({
  appt,
  variant = 'popup',
  onEdit,
  onFollowUp,
  onReschedule,
  onAfterMutation,
  hideCancel = false,
  cancelOpen: cancelOpenProp,
  onCancelOpenChange,
  onStatusPendingChange,
}: AppointmentStatusManagerProps) {
  const queryClient = useQueryClient();
  const { data: statusList } = useStatusList();
  const updateStatus = useUpdateAppointmentStatus();

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpenOwn, setCancelOpenOwn] = useState(false);
  const cancelOpen = cancelOpenProp ?? cancelOpenOwn;
  const setCancelOpen = (next: boolean) => {
    setCancelOpenOwn(next);
    onCancelOpenChange?.(next);
  };
  // Mark-Arrived incomplete-patient-data guard (PROD-989). When set, the
  // Arrived transition is blocked and this alert is shown instead; the user
  // can proceed to complete the patient (→ onEdit) or cancel.
  const [arrivedAlertKind, setArrivedAlertKind] = useState<ArrivedGuardKind>(null);

  const actions = useMemo<AppointmentAction[]>(() => {
    if (variant === 'panel') {
      // Left edit-panel menu — 3-day window gate, full-word labels, no
      // home/reschedule/follow-up (Flutter create_appointment_widget.dart).
      const base = getPanelActionsFor({
        statusCode: appt.status?.code ?? '',
        inStatusWindow: isWithinStatusButtonWindow(appt.start),
        isRecurring: !!appt.recurring?.id,
      });
      return hideCancel ? base.filter((a) => a.id !== 'cancel') : base;
    }
    const ctx = {
      statusCode: appt.status?.code ?? '',
      isToday: isToday(appt.start),
      isHomeLocation: (appt.location?.name ?? '').toLowerCase() === 'home',
      isRecurring: !!appt.recurring?.id,
    };
    const base = variant === 'row' ? getRowActionsFor(ctx) : getPopupActionsFor(ctx);
    return hideCancel ? base.filter((a) => a.id !== 'cancel') : base;
  }, [appt.status?.code, appt.start, appt.location?.name, appt.recurring?.id, variant, hideCancel]);

  // A parent-owned Cancel (`cancelOpen` passed) needs the dialogs mounted even when no footer action is left.
  if (actions.length === 0 && cancelOpenProp === undefined) return null;

  const handle = (action: AppointmentAction) => () => {
    const newCode = statusCodeForAction(action.id);
    if (newCode) {
      if (updateStatus.isPending) return;
      // Mark-Arrived guard (PROD-989) — block the transition when the
      // patient's identity/guardian data is incomplete and prompt to
      // complete the patient first (Flutter `showIncompletePatientDataAlert`).
      if (action.id === 'mark-arrived') {
        const { blocked, kind } = arrivedGuardStatus(
          guardPatientFromAppointment(appt),
        );
        if (blocked) {
          setArrivedAlertKind(kind);
          return;
        }
      }
      const target = statusList?.find(
        (s) => (s.code ?? '').toLowerCase() === newCode.toLowerCase(),
      );
      if (!target) return;
      onStatusPendingChange?.(true);
      updateStatus.mutate(
        { appointmentId: appt.id, status: target as AppointmentStatus },
        {
          onSettled: () => onStatusPendingChange?.(false),
          onSuccess: () => {
            // Popup / panel keep their own preload cache — refresh so the
            // header badge + billing-tab gate reflect the new status.
            queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
            onAfterMutation?.();
          },
        },
      );
      return;
    }
    if (action.id === 'reschedule') {
      // Reschedule opens the inline left edit panel when the consumer wires it
      // (Flutter parity — reschedule is a left-panel edit, never a dialog; see
      // `calendar_tab.dart:2242-2253`). The fallback date/time dialog moves the
      // single occurrence. No recurring-scope prompt: Flutter never shows one
      // on the reschedule action (the scope picker lives only in the recurring
      // series edit menu, wired to EditRecurringAppointmentEvent — not here).
      if (onReschedule) {
        onReschedule();
        return;
      }
      setRescheduleOpen(true);
      return;
    }
    if (action.id === 'cancel') return setCancelOpen(true);
    if (action.id === 'edit') return onEdit?.();
    // Follow-up (PROD-987): delegate to the consumer, which opens the
    // create panel pre-filled from this fulfilled appointment. Visibility
    // (Fulfilled + non-recurring) is already enforced by getPopupActionsFor.
    if (action.id === 'follow-up') return onFollowUp?.();
    // 'overflow' / 'add-note' are handled by the row consumer when the row
    // variant ships; ignore on the popup variant.
  };

  return (
    <>
      {variant === 'panel' ? (
        <div className="flex flex-col gap-3 px-4 pt-2">
          {actions.map((action) => (
            <PanelActionButton
              key={action.id}
              label={action.label}
              tone={action.tone}
              disabled={updateStatus.isPending}
              onClick={handle(action)}
            />
          ))}
        </div>
      ) : (
        actions.map((action) => {
          const BridgeIcon = ACTION_ICON[action.id];
          const label = SHORT_LABEL[action.id] ?? action.label;
          return (
            <Button
              key={action.id}
              variant={action.tone === 'error' ? 'destructive' : 'secondary'}
              size="sm"
              disabled={updateStatus.isPending}
              onClick={handle(action)}
            >
              {BridgeIcon && <BridgeIcon className="mr-2 h-4 w-4" strokeWidth={2} />}
              {label}
            </Button>
          );
        })
      )}

      {/* Reschedule fallback dialog (single occurrence). Reached only when no
          consumer wires `onReschedule` to the inline panel. No recurring-scope
          prompt — Flutter never shows one on the reschedule action. */}
      <RescheduleAppointmentDialog
        open={rescheduleOpen}
        onOpenChange={(next) => {
          setRescheduleOpen(next);
          if (!next) {
            queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
            onAfterMutation?.();
          }
        }}
        target={{
          appointmentId: appt.id,
          startISO: appt.start ?? '',
          endISO: appt.end ?? '',
        }}
      />
      <CancelAppointmentDialog
        open={cancelOpen}
        onOpenChange={(next) => {
          setCancelOpen(next);
          if (!next) {
            queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
            onAfterMutation?.();
          }
        }}
        target={{
          appointmentId: appt.id,
          patientId: findPatientParticipant(appt.participant)?.patient?.id,
          practitionerId: appt.participant?.find((p) => {
            const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
            return t === 'practitioner' || t === 'provider';
          })?.provider?.id,
          startISO: appt.start ?? undefined,
          recurringId: appt.recurring?.id ?? undefined,
          patientFirstName: findPatientParticipant(appt.participant)?.patient?.firstName,
        }}
      />

      {/* Mark-Arrived incomplete-patient-data guard (PROD-989). "Proceed to
          complete patient" routes to the edit flow (onEdit) — same as
          Flutter's `showIncompletePatientDataAlert` → `onSelect(edit)`. */}
      <AlertDialog
        open={arrivedAlertKind !== null}
        onOpenChange={(next) => {
          if (!next) setArrivedAlertKind(null);
        }}
      >
        <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
          <AlertDialogHeader>
            <AlertDialogTitle>{ARRIVED_GUARD_TITLE}</AlertDialogTitle>
            <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
              {arrivedGuardMessage(arrivedAlertKind)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setArrivedAlertKind(null);
                onEdit?.();
              }}
            >
              {ARRIVED_GUARD_PROCEED}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Internals ─────────────────────────────────────────────────────────

const TONE_CLASS: Record<ActionTone, string> = {
  success: 'text-[var(--color-status-success-text)]',
  warning: 'text-[var(--color-status-warning-text)]',
  info: 'text-brand-primary',
  error: 'text-status-error',
  muted: 'text-text-muted',
};

interface PanelActionButtonProps {
  label: string;
  tone: ActionTone;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * PanelActionButton — full-width text button for the left edit-panel status
 * menu (Flutter `create_appointment_widget.dart` `Button(width: 256, height:
 * 32, buttonBackgroundColor: bgColor, ...)`). Light-grey fill (`bgColor` =
 * lightGrey), bold coloured label by tone: transitions brand-blue
 * (`enabledBtnBGColor`), Unmark No Show green (`successTabColor`), Cancel red.
 */
function PanelActionButton({ label, tone, disabled, onClick }: PanelActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'w-full rounded-md bg-grey-100 py-2 text-sm font-semibold text-center transition-colors hover:bg-grey-200 disabled:opacity-50 disabled:cursor-not-allowed',
        TONE_CLASS[tone],
      )}
    >
      {label}
    </button>
  );
}

